import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AtSign, Pencil, Eraser, AlertTriangle, CheckCircle2, RefreshCw, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ReservationsManager } from "@/components/admin/ReservationsManager";
import { ReservedWordsManager } from "@/components/admin/ReservedWordsManager";
import { Switch } from "@/components/ui/switch";

const PAGE_SIZE = 50;
const TYPE_LABEL = { personal: "Person", business: "Business", capital: "Capital" };
const SLUG_PREFIX = { personal: "startupotter.com/p/", business: "startupotter.com/b/", capital: "startupotter.com/capital/" };
const TYPE_CLS = {
  personal: "bg-teal-50 text-teal-700",
  business: "bg-indigo-50 text-indigo-700",
  capital: "bg-amber-50 text-amber-700",
};

function TypeBadge({ t }) {
  return <Badge variant="secondary" className={`shrink-0 border-0 font-[560] ${TYPE_CLS[t] || "bg-secondary"}`}>{TYPE_LABEL[t] || t}</Badge>;
}

export default function AdminHandles() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);

  const [conflicts, setConflicts] = useState(null);
  const [edit, setEdit] = useState(null); // {profile_type, profile_id, display_name, handle, slug}
  const [editHandle, setEditHandle] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [forceEdit, setForceEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearTarget, setClearTarget] = useState(null);

  const bump = () => setRefresh((k) => k + 1);

  // Debounce the search box -> resets to page 1
  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Handles list
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/handles?q=${encodeURIComponent(q)}&page=${page}&page_size=${PAGE_SIZE}`);
        if (active) { setItems(data.items || []); setTotal(data.total || 0); }
      } catch { if (active) { setItems([]); setTotal(0); } }
    })();
    return () => { active = false; };
  }, [q, page, refresh]);

  // Conflicts
  useEffect(() => {
    let active = true;
    (async () => {
      try { const { data } = await api.get("/admin/handles/conflicts"); if (active) setConflicts(data); }
      catch { if (active) setConflicts({ duplicates: [], missing_from_registry: [] }); }
    })();
    return () => { active = false; };
  }, [refresh]);

  const openEdit = (p) => { setEdit(p); setEditHandle(p.handle || ""); setEditSlug(p.slug || ""); setForceEdit(false); };

  const save = async () => {
    if (!edit) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/identity/assign", {
        profile_type: edit.profile_type, profile_id: edit.profile_id,
        handle: editHandle.trim(), slug: editSlug.trim(), force: forceEdit,
      });
      const disp = data?.displaced || [];
      if (disp.length) toast.warning(`Displaced ${disp.length}: ${disp.map((x) => `${x.display_name || x.profile_id} → ${x.new_value}`).join(", ")}`);
      toast.success("Saved");
      setEdit(null); bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const doClear = async () => {
    const p = clearTarget;
    try {
      await api.put("/admin/handles", { profile_type: p.profile_type, profile_id: p.profile_id, handle: "" });
      toast.success("Handle cleared"); setClearTarget(null); bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clear handle"); }
  };

  const register = async (m) => {
    try { await api.put("/admin/handles", { profile_type: m.profile_type, profile_id: m.profile_id, handle: m.handle }); toast.success("Added to registry"); bump(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Handle is already taken — change it instead"); }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const dups = conflicts?.duplicates || [];
  const missing = conflicts?.missing_from_registry || [];
  const hasConflicts = dups.length > 0 || missing.length > 0;

  return (
    <div data-testid="admin-handles" className="space-y-6">
      <Tabs defaultValue="registry">
        <TabsList data-testid="admin-handles-tabs">
          <TabsTrigger value="registry" data-testid="admin-handles-tab-registry">Registry</TabsTrigger>
          <TabsTrigger value="reservations" data-testid="admin-handles-tab-reservations">Reservations</TabsTrigger>
          <TabsTrigger value="reserved-words" data-testid="admin-handles-tab-words">Reserved words</TabsTrigger>
        </TabsList>

        <TabsContent value="registry" className="mt-5 space-y-6">
          {/* Conflicts */}
          <Card className="border-0 p-5" data-testid="admin-handles-conflicts">
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground">
            {hasConflicts ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
            Registry health
          </h2>
          <Button size="sm" variant="ghost" onClick={bump} data-testid="admin-handles-refresh"><RefreshCw className="mr-1.5 h-4 w-4" /> Refresh</Button>
        </div>

        {conflicts === null ? (
          <div className="py-6"><Loading /></div>
        ) : !hasConflicts ? (
          <p className="mt-2 text-sm text-muted-foreground">Every handle is globally unique and present in the registry. Nothing to resolve. 🎉</p>
        ) : (
          <div className="mt-4 space-y-5">
            {dups.length > 0 && (
              <div>
                <p className="text-sm font-[600] text-foreground">Duplicate handles <span className="text-muted-foreground">({dups.length})</span></p>
                <p className="text-xs text-muted-foreground">The same handle (case-insensitive) is claimed by more than one profile. Change all but one.</p>
                <div className="mt-3 space-y-3">
                  {dups.map((d) => (
                    <div key={d.handle_norm} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid={`handle-dup-${d.handle_norm}`}>
                      <div className="text-sm font-[600] text-amber-800">@{d.handle_norm}</div>
                      <div className="mt-2 divide-y divide-amber-100">
                        {d.profiles.map((p) => (
                          <div key={p.profile_id} className="flex items-center gap-2 py-2">
                            <TypeBadge t={p.profile_type} />
                            <span className="min-w-0 flex-1 truncate text-sm text-foreground">{p.display_name || "—"} <span className="text-muted-foreground">· @{p.handle}</span></span>
                            <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`handle-conflict-fix-${p.profile_id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Change</Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {missing.length > 0 && (
              <div>
                <p className="text-sm font-[600] text-foreground">Missing from registry <span className="text-muted-foreground">({missing.length})</span></p>
                <p className="text-xs text-muted-foreground">These profiles have a handle on the profile record but no registry entry (so they can’t be resolved/mentioned).</p>
                <div className="mt-3 divide-y divide-border/70 rounded-lg border border-border p-1">
                  {missing.map((p) => (
                    <div key={p.profile_id} className="flex items-center gap-2 px-2 py-2" data-testid={`handle-missing-${p.profile_id}`}>
                      <TypeBadge t={p.profile_type} />
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{p.display_name || "—"} <span className="text-muted-foreground">· @{p.handle}</span></span>
                      <Button size="sm" onClick={() => register(p)} data-testid={`handle-conflict-register-${p.profile_id}`}>Add to registry</Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`handle-conflict-fix-${p.profile_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Registry list */}
      <Card className="border-0 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-[640] text-foreground">Handle registry</h2>
          <div className="relative w-full max-w-xs">
            <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search handle or slug…" data-testid="admin-handles-search" />
          </div>
        </div>

        {!items ? <div className="mt-6"><Loading /></div> : items.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">{q ? "No handles match your search." : "No handles registered yet."}</p>
        ) : (
          <>
            <div className="mt-4 divide-y divide-border/70">
              {items.map((h) => (
                <div key={h.profile_id} className="flex items-center gap-3 py-2.5" data-testid={`handle-row-${h.profile_id}`}>
                  <TypeBadge t={h.profile_type} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Link to={`/@${h.handle}`} className="truncate text-sm font-[600] text-primary hover:underline" data-testid={`handle-link-${h.profile_id}`}>@{h.handle}</Link>
                      {!h.published && <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-[560] text-muted-foreground">unpublished</span>}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">{h.display_name || "—"}{h.slug ? <span className="font-mono"> · /{h.slug}</span> : null}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(h)} data-testid={`handle-edit-${h.profile_id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setClearTarget(h)} data-testid={`handle-clear-${h.profile_id}`}><Eraser className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{total} handle{total === 1 ? "" : "s"} · page {page} of {pages}</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="admin-handles-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} data-testid="admin-handles-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </>
        )}
      </Card>
        </TabsContent>

        <TabsContent value="reservations" className="mt-5">
          <ReservationsManager />
        </TabsContent>

        <TabsContent value="reserved-words" className="mt-5">
          <ReservedWordsManager />
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit handle &amp; slug</DialogTitle>
            <DialogDescription>{edit?.display_name || "Profile"} · {TYPE_LABEL[edit?.profile_type] || ""}. Edit either field, or clear one (or both) to make it available for others to claim. Handles are global; slugs are scoped to this profile type. No redirects — a cleared slug is immediately claimable.</DialogDescription>
          </DialogHeader>

          {/* Handle */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-[560] text-foreground">Handle</span>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => setEditHandle("")} disabled={!editHandle} data-testid="handle-edit-free-handle"><Eraser className="mr-1 h-3.5 w-3.5" /> Make available</Button>
            </div>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"><AtSign className="h-4 w-4" /></span>
              <Input
                value={editHandle}
                onChange={(e) => setEditHandle(e.target.value.replace(/^@+/, "").replace(/[^a-zA-Z0-9-]/g, "").slice(0, 50))}
                placeholder="handle (leave blank to free)"
                className="pl-9"
                autoFocus
                data-testid="handle-edit-input"
              />
            </div>
          </div>

          {/* Slug */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-[560] text-foreground">URL slug</span>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => setEditSlug("")} disabled={!editSlug} data-testid="handle-edit-free-slug"><Eraser className="mr-1 h-3.5 w-3.5" /> Make available</Button>
            </div>
            <Input
              value={editSlug}
              onChange={(e) => setEditSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 80))}
              placeholder="slug (leave blank to free)"
              data-testid="slug-edit-input"
            />
            <p className="truncate text-xs text-muted-foreground">{SLUG_PREFIX[edit?.profile_type] || "startupotter.com/p/"}<span className="font-mono text-foreground">{editSlug || "…"}</span></p>
          </div>

          {/* Force takeover */}
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
            <Switch checked={forceEdit} onCheckedChange={setForceEdit} data-testid="handle-edit-force" />
            <span className="text-xs text-amber-800">Force takeover — if a value is already in use, displace the current holder (they get a random name).</span>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="handle-edit-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear confirm */}
      <AlertDialog open={!!clearTarget} onOpenChange={(o) => !o && setClearTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear @{clearTarget?.handle}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the handle from {clearTarget?.display_name || "this profile"} and frees it for anyone to claim. Existing mentions will keep their original text. This can’t be undone automatically.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doClear} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="handle-clear-confirm">Clear handle</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
